/**
 * PIN-protected backup of identity material, modelled on Signal's
 * SecureValueRecovery (SVR). Mirrors the iOS implementation in
 * `kaether_for_ios/Stoat/E2EE/SecureValueRecovery.swift` byte-for-byte so
 * a backup created on iOS can be opened on web and vice-versa.
 *
 * Two layers of indirection allow PIN changes without re-encrypting the blob:
 *
 *   PIN ──PBKDF2──▶ KEK ──AES-GCM(masterKey)──▶ encMasterKey
 *   masterKey      ──AES-GCM(blob)──▶ encBlob
 *
 * The server (KDS endpoint `/users/me/svr`) stores only the SealedBundle JSON.
 * It never sees the PIN, the KEK, the masterKey, or the plaintext blob.
 *
 * Forward secrecy: the blob deliberately excludes Double-Ratchet sessions and
 * one-time prekeys. Restoring on a new device recovers identity (so peers'
 * trust pins still match) but cannot decrypt past traffic.
 *
 * Browser requirements: SubtleCrypto (Web Crypto API) available in all modern
 * browsers and in Node.js >= 15. Specifically uses:
 *   - `crypto.subtle.importKey` / `deriveKey` / `deriveBits` (PBKDF2)
 *   - `crypto.subtle.encrypt` / `decrypt` (AES-GCM)
 *   - `crypto.getRandomValues` (salt + nonce generation)
 *
 * @module SecureValueRecovery
 */

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

/** Thrown by seal() / open() / rewrap() when the PIN fails policy. */
export class PolicyError extends Error {
  readonly code: "PIN_TOO_SHORT";
  constructor(message: string) {
    super(message);
    this.name = "PolicyError";
    this.code = "PIN_TOO_SHORT";
  }
}

/**
 * Thrown by open() / rewrap() when the GCM auth tag on encMasterKey fails.
 * Surfaces as a user-facing "wrong PIN" error (mirrors iOS SVRError.wrongPin).
 * Distinguishing a genuinely wrong PIN from a corrupted encMasterKey field
 * requires server-side metadata we don't have, so we surface the more useful
 * UX message here.
 */
export class WrongPinError extends Error {
  constructor() {
    super("Wrong PIN — GCM authentication failed on encMasterKey");
    this.name = "WrongPinError";
  }
}

/**
 * Thrown by open() when GCM auth on encBlob fails or the decrypted bytes
 * cannot be parsed as a valid SVRBackupBlob. Indicates server-side or
 * in-transit data corruption (mirrors iOS SVRError.corruptedBackup).
 */
export class TamperedError extends Error {
  constructor(detail?: string) {
    super(detail ?? "Backup is corrupted — GCM authentication failed on encBlob");
    this.name = "TamperedError";
  }
}

/** Thrown when the KDF algorithm discriminator in a bundle is unknown. */
export class UnsupportedAlgorithmError extends Error {
  readonly algorithm: string;
  constructor(algorithm: string) {
    super(`Unsupported KDF algorithm: "${algorithm}"`);
    this.name = "UnsupportedAlgorithmError";
    this.algorithm = algorithm;
  }
}

// ---------------------------------------------------------------------------
// KDF algorithm discriminators
// ---------------------------------------------------------------------------

/**
 * Closed set of supported KDF algorithms. The discriminator is stored in the
 * SealedBundle so a future Argon2id-via-WASM upgrade can open legacy PBKDF2
 * bundles without breaking compatibility.
 *
 * Mirrors the iOS enum discriminator strings in SecureValueRecovery.swift.
 */
export type KDFAlgorithm = "PBKDF2-SHA256"; // Argon2id added when WASM ships

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * KDF parameters persisted alongside the ciphertext. Changing any field
 * requires re-sealing because it is part of the AAD (so the GCM tag covers
 * the parameters).
 */
export interface SVRKDFParams {
  /** Algorithm discriminator — allows future upgrade without breaking reads. */
  readonly algorithm: KDFAlgorithm;
  /** PBKDF2 iteration count. Production: 600_000. Tests may use 1_000. */
  readonly iterations: number;
  /** Derived key length in bytes. Always 32 (AES-256). */
  readonly keyBytes: number;
  /** Salt length in bytes. Always 16. */
  readonly saltBytes: number;
}

/** Production parameters: PBKDF2-SHA256 at 600k iterations (OWASP 2023). */
export const PRODUCTION_KDF_PARAMS: SVRKDFParams = {
  algorithm: "PBKDF2-SHA256",
  iterations: 600_000,
  keyBytes: 32,
  saltBytes: 16,
} as const;

/**
 * What we actually back up. Sessions are intentionally absent — see module
 * header for the forward-secrecy rationale.
 */
export interface SVRBackupBlob {
  /** Schema version. Currently 1. */
  readonly version: number;
  /** Raw bytes of the identity public key (Curve25519, 32 bytes). */
  readonly identityPublicKey: Uint8Array;
  /** Raw bytes of the identity private key (Curve25519, 32 bytes). */
  readonly identityPrivateKey: Uint8Array;
  /** Signal registration ID (random 14-bit integer). */
  readonly registrationId: number;
  /** Device ID. Web = 1. */
  readonly deviceId: number;
  /**
   * Trusted identity public keys keyed by userId string. These are the TOFU
   * pins — restoring them on a new device means peers still recognise the
   * returned identity without a trust-change banner.
   */
  readonly trustedIdentities: Readonly<Record<string, Uint8Array>>;
}

/** Current schema version. */
export const SVR_BLOB_VERSION = 1 as const;

/**
 * What the KDS server stores. Everything sensitive is sealed under AES-GCM.
 *
 * Wire format (JSON-serialised):
 *   {
 *     version:      number          // bundle schema version, currently 1
 *     kdf: {
 *       algorithm:  string          // KDF discriminator e.g. "PBKDF2-SHA256"
 *       iterations: number
 *       keyBytes:   number
 *       saltBytes:  number
 *     }
 *     salt:         string          // base64url, 16 bytes — KEK derivation salt
 *     encMasterKey: string          // base64url, AES-GCM(masterKey, key=KEK)
 *                                  //   combined: nonce(12) | ciphertext(32) | tag(16) = 60 bytes
 *     encBlob:      string          // base64url, AES-GCM(JSON(blob), key=masterKey)
 *                                  //   combined: nonce(12) | ciphertext(??) | tag(16)
 *     createdAt:    string          // ISO-8601 timestamp
 *   }
 *
 * The AAD for both AES-GCM calls is the canonical header bytes:
 *   UTF-8("KAETHER-SVR-V1") || 0x01 (version) || algorithm bytes (null-padded to 32)
 *   || salt (16 bytes) || iterations as big-endian uint32
 * This ensures that flipping any header field (version, algorithm, salt,
 * iterations) invalidates the GCM authentication tag, mirroring the iOS AAD.
 */
export interface SealedBundle {
  /** Bundle schema version. Currently 1. */
  readonly version: number;
  readonly kdf: SVRKDFParams;
  /** Base64url-encoded 16-byte salt for KEK derivation. */
  readonly salt: string;
  /**
   * Base64url-encoded AES-GCM combined (nonce | ciphertext | tag) of the
   * 32-byte masterKey, encrypted under the PIN-derived KEK.
   */
  readonly encMasterKey: string;
  /**
   * Base64url-encoded AES-GCM combined (nonce | ciphertext | tag) of the
   * JSON-serialised SVRBackupBlob, encrypted under the masterKey.
   */
  readonly encBlob: string;
  /** ISO-8601 creation timestamp. */
  readonly createdAt: string;
}

/** Current bundle schema version. */
export const SVR_BUNDLE_VERSION = 1 as const;

// ---------------------------------------------------------------------------
// PIN policy
// ---------------------------------------------------------------------------

/**
 * Minimum PIN length after normalisation. Mirrors iOS SecureValueRecovery
 * minPinLength = 4.
 */
export const MIN_PIN_LENGTH = 4 as const;

/**
 * Normalise a PIN before hashing: trim leading/trailing whitespace and apply
 * Unicode NFKC (compatibility decomposition + canonical composition) so that
 * "1234" entered with different keyboard variants hashes identically.
 *
 * Intentionally does NOT lowercase — alphanumeric PINs are case-sensitive on
 * both iOS and web.
 *
 * Mirrors iOS SecureValueRecovery.normalize(pin:).
 */
export function normalizePin(pin: string): string {
  return pin.trim().normalize("NFKC");
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Encode bytes as base64url (no padding). */
function toBase64url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/** Decode a base64url string (with or without padding) to bytes. */
function fromBase64url(s: string): Uint8Array {
  // Restore standard base64 padding.
  const padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (padded.length % 4)) % 4;
  const b64 = padded + "=".repeat(padLen);
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Serialise SVRBackupBlob to a JSON Uint8Array. Uint8Arrays → base64url. */
function encodeBlob(blob: SVRBackupBlob): Uint8Array {
  const obj = {
    version: blob.version,
    identityPublicKey: toBase64url(blob.identityPublicKey),
    identityPrivateKey: toBase64url(blob.identityPrivateKey),
    registrationId: blob.registrationId,
    deviceId: blob.deviceId,
    trustedIdentities: Object.fromEntries(
      Object.entries(blob.trustedIdentities).map(([k, v]) => [k, toBase64url(v)]),
    ),
  };
  return new TextEncoder().encode(JSON.stringify(obj));
}

/** Deserialise a JSON Uint8Array back to SVRBackupBlob. */
function decodeBlob(bytes: Uint8Array): SVRBackupBlob {
  const obj = JSON.parse(new TextDecoder().decode(bytes)) as {
    version: number;
    identityPublicKey: string;
    identityPrivateKey: string;
    registrationId: number;
    deviceId: number;
    trustedIdentities: Record<string, string>;
  };
  return {
    version: obj.version,
    identityPublicKey: fromBase64url(obj.identityPublicKey),
    identityPrivateKey: fromBase64url(obj.identityPrivateKey),
    registrationId: obj.registrationId,
    deviceId: obj.deviceId,
    trustedIdentities: Object.fromEntries(
      Object.entries(obj.trustedIdentities).map(([k, v]) => [k, fromBase64url(v)]),
    ),
  };
}

/**
 * Build the canonical AAD bytes that both GCM calls share.
 *
 * Layout (total 57 bytes):
 *   [0..13]   UTF-8("KAETHER-SVR-V1")                   — 14 bytes magic
 *   [14]      bundle version uint8                        — 1 byte
 *   [15..46]  algorithm string, UTF-8, zero-padded to 32  — 32 bytes
 *   [47..62]  salt                                        — 16 bytes
 *   [63..66]  iterations big-endian uint32                — 4 bytes
 *
 * Any tampering with the header fields causes the GCM tag to fail, because
 * the AAD is authenticated even though it is not encrypted.
 */
function buildAAD(params: SVRKDFParams, salt: Uint8Array): Uint8Array {
  const magic = new TextEncoder().encode("KAETHER-SVR-V1");
  const algoBytes = new Uint8Array(32);
  const encodedAlgo = new TextEncoder().encode(params.algorithm);
  algoBytes.set(encodedAlgo.subarray(0, Math.min(32, encodedAlgo.length)));

  const aad = new Uint8Array(14 + 1 + 32 + 16 + 4);
  let offset = 0;
  aad.set(magic, offset); offset += 14;
  aad[offset] = SVR_BUNDLE_VERSION; offset += 1;
  aad.set(algoBytes, offset); offset += 32;
  aad.set(salt, offset); offset += 16;
  // iterations as big-endian uint32
  const view = new DataView(aad.buffer, aad.byteOffset + offset, 4);
  view.setUint32(0, params.iterations, false /* big-endian */);

  return aad;
}

/**
 * Derive the Key Encryption Key (KEK) from the PIN and salt using PBKDF2-SHA256.
 *
 * The resulting CryptoKey is AES-GCM-capable only; it is never extractable.
 * Mirrors iOS CCKeyDerivationPBKDF(kCCPBKDF2, kCCPRFHmacAlgSHA256, ...).
 */
async function deriveKEK(
  pin: string,
  salt: Uint8Array,
  params: SVRKDFParams,
): Promise<CryptoKey> {
  if (params.algorithm !== "PBKDF2-SHA256") {
    throw new UnsupportedAlgorithmError(params.algorithm);
  }

  const pinBytes = new TextEncoder().encode(normalizePin(pin));

  const baseKey = await crypto.subtle.importKey(
    "raw",
    pinBytes,
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt,
      iterations: params.iterations,
    },
    baseKey,
    { name: "AES-GCM", length: params.keyBytes * 8 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** Generate `n` random bytes via `crypto.getRandomValues`. */
function randomBytes(n: number): Uint8Array {
  const buf = new Uint8Array(n);
  crypto.getRandomValues(buf);
  return buf;
}

/**
 * AES-GCM encrypt. Returns combined bytes: nonce(12) | ciphertext | tag(16).
 * The `aad` parameter is authenticated but not encrypted.
 */
async function aesgcmSeal(
  plaintext: Uint8Array,
  key: CryptoKey,
  aad: Uint8Array,
): Promise<Uint8Array> {
  const nonce = randomBytes(12);
  const ciphertextWithTag = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce, additionalData: aad },
    key,
    plaintext,
  );
  const combined = new Uint8Array(12 + ciphertextWithTag.byteLength);
  combined.set(nonce, 0);
  combined.set(new Uint8Array(ciphertextWithTag), 12);
  return combined;
}

/**
 * AES-GCM decrypt. Expects combined bytes: nonce(12) | ciphertext | tag(16).
 * Throws a raw DOMException on authentication failure; callers translate this
 * into WrongPinError or TamperedError as appropriate.
 */
async function aesgcmOpen(
  combined: Uint8Array,
  key: CryptoKey,
  aad: Uint8Array,
): Promise<Uint8Array> {
  const nonce = combined.subarray(0, 12);
  const ciphertextWithTag = combined.subarray(12);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonce, additionalData: aad },
    key,
    ciphertextWithTag,
  );
  return new Uint8Array(plaintext);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Seal a backup blob under a PIN.
 *
 * @param blob     The identity material to back up.
 * @param pin      The user-supplied PIN. Normalised (trim + NFKC) before use.
 * @param params   KDF parameters. Defaults to production 600k-iteration params.
 * @returns        A SealedBundle safe to upload to `/users/me/svr` on KDS.
 * @throws PolicyError if the normalised PIN is shorter than MIN_PIN_LENGTH (4).
 */
export async function seal(
  blob: SVRBackupBlob,
  pin: string,
  params: SVRKDFParams = PRODUCTION_KDF_PARAMS,
): Promise<SealedBundle> {
  const normalised = normalizePin(pin);
  if (normalised.length < MIN_PIN_LENGTH) {
    throw new PolicyError(
      `PIN must be at least ${MIN_PIN_LENGTH} characters after normalisation (got ${normalised.length})`,
    );
  }

  // Fresh random salt per backup → same PIN produces different ciphertexts.
  const salt = randomBytes(params.saltBytes);
  const kek = await deriveKEK(normalised, salt, params);
  const aad = buildAAD(params, salt);

  // Generate a random 256-bit master key.
  const masterKeyRaw = randomBytes(32);
  const masterKey = await crypto.subtle.importKey(
    "raw",
    masterKeyRaw,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );

  // Seal the master key under the KEK.
  const encMasterKeyBytes = await aesgcmSeal(masterKeyRaw, kek, aad);

  // Seal the blob JSON under the master key.
  const blobBytes = encodeBlob(blob);
  const encBlobBytes = await aesgcmSeal(blobBytes, masterKey, aad);

  return {
    version: SVR_BUNDLE_VERSION,
    kdf: params,
    salt: toBase64url(salt),
    encMasterKey: toBase64url(encMasterKeyBytes),
    encBlob: toBase64url(encBlobBytes),
    createdAt: new Date().toISOString(),
  };
}

/**
 * Open a sealed bundle with the user's PIN, recovering the original blob.
 *
 * @throws WrongPinError          if the PIN fails the encMasterKey GCM check.
 * @throws TamperedError          if the PIN is correct but encBlob fails auth,
 *                                 or if the decrypted bytes cannot be parsed.
 * @throws UnsupportedAlgorithmError if the bundle uses an unknown KDF.
 * @throws PolicyError            if the normalised PIN is too short.
 */
export async function open(bundle: SealedBundle, pin: string): Promise<SVRBackupBlob> {
  const normalised = normalizePin(pin);
  if (normalised.length < MIN_PIN_LENGTH) {
    throw new PolicyError(
      `PIN must be at least ${MIN_PIN_LENGTH} characters after normalisation (got ${normalised.length})`,
    );
  }

  const salt = fromBase64url(bundle.salt);
  const kek = await deriveKEK(normalised, salt, bundle.kdf);
  const aad = buildAAD(bundle.kdf, salt);

  // Attempt to unwrap the master key. GCM failure here = wrong PIN.
  let masterKeyRaw: Uint8Array;
  try {
    masterKeyRaw = await aesgcmOpen(fromBase64url(bundle.encMasterKey), kek, aad);
  } catch {
    throw new WrongPinError();
  }

  // Import master key for blob decryption.
  const masterKey = await crypto.subtle.importKey(
    "raw",
    masterKeyRaw,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"],
  );

  // Attempt to decrypt the blob. GCM failure here = tampered data.
  let blobBytes: Uint8Array;
  try {
    blobBytes = await aesgcmOpen(fromBase64url(bundle.encBlob), masterKey, aad);
  } catch {
    throw new TamperedError();
  }

  // Parse the JSON. Failure here also = tampered / corrupted data.
  try {
    return decodeBlob(blobBytes);
  } catch (e) {
    throw new TamperedError(
      `Blob JSON parse failed: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
}

/**
 * Change the PIN protecting an existing bundle without re-encrypting the blob.
 *
 * The masterKey is unwrapped with the old PIN's KEK and immediately re-wrapped
 * with a fresh salt + the new PIN's KEK. The encBlob field is copied unchanged.
 * This means a PIN change is cheap (no blob re-encryption) and forward-secret
 * (the old PIN can no longer open the bundle after rewrap).
 *
 * @throws WrongPinError          if oldPin fails the encMasterKey GCM check.
 * @throws PolicyError            if either PIN fails policy.
 * @throws UnsupportedAlgorithmError if the bundle uses an unknown KDF.
 */
export async function rewrap(
  bundle: SealedBundle,
  oldPin: string,
  newPin: string,
  params: SVRKDFParams = PRODUCTION_KDF_PARAMS,
): Promise<SealedBundle> {
  const normNew = normalizePin(newPin);
  if (normNew.length < MIN_PIN_LENGTH) {
    throw new PolicyError(
      `New PIN must be at least ${MIN_PIN_LENGTH} characters after normalisation (got ${normNew.length})`,
    );
  }

  // Validate old PIN by unwrapping the master key.
  const oldSalt = fromBase64url(bundle.salt);
  const oldKek = await deriveKEK(normalizePin(oldPin), oldSalt, bundle.kdf);
  const oldAad = buildAAD(bundle.kdf, oldSalt);

  let masterKeyRaw: Uint8Array;
  try {
    masterKeyRaw = await aesgcmOpen(fromBase64url(bundle.encMasterKey), oldKek, oldAad);
  } catch {
    throw new WrongPinError();
  }

  // Fresh salt + KEK for the new PIN.
  const newSalt = randomBytes(params.saltBytes);
  const newKek = await deriveKEK(normNew, newSalt, params);
  const newAad = buildAAD(params, newSalt);

  const newEncMasterKeyBytes = await aesgcmSeal(masterKeyRaw, newKek, newAad);

  // The blob ciphertext must be re-encrypted too because the AAD (which
  // includes the salt) changes. Decrypt with old master key + old AAD,
  // then re-encrypt with same master key + new AAD.
  const masterKey = await crypto.subtle.importKey(
    "raw",
    masterKeyRaw,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );

  let blobBytes: Uint8Array;
  try {
    blobBytes = await aesgcmOpen(fromBase64url(bundle.encBlob), masterKey, oldAad);
  } catch {
    throw new TamperedError("encBlob authentication failed during rewrap");
  }

  const newEncBlobBytes = await aesgcmSeal(blobBytes, masterKey, newAad);

  return {
    version: bundle.version,
    kdf: params,
    salt: toBase64url(newSalt),
    encMasterKey: toBase64url(newEncMasterKeyBytes),
    encBlob: toBase64url(newEncBlobBytes),
    createdAt: bundle.createdAt,
  };
}
